/**
 * Accordion component will expand and collapse on click which shows and hides details under a heading.
 *
 * @module views/components/Accordion
 * @memberof -Common
 */
import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import PaletteAccordion from 'web-palette/dist/components/Accordion/Accordion';

import constants from '../../utils/constants/constants';
import datacapture from '../../utils/datacapture/datacapture';
import * as utils from './Accordion';

/**
  * Represents an Accordion component
  * @method
  * @param {AccordionProps} props - React properties passed from composition
  * @returns PalAccordion
  */
const Accordion = ( props ) => {
  const {
    ariaControls = defaultProps.ariaControls,
    ariaLabelledBy = defaultProps.ariaLabelledBy,
    isActive = defaultProps.isActive,
    headerChildren,
    subTitle,
    titleTag = defaultProps.titleTag,
    title,
    children,
    collapseAction,
    disabled = defaultProps.disabled,
    expandAction,
    expandMore = defaultProps.expandMore,
    setExpandMore = defaultProps.setExpandMore,
    setIsActive,
    icon,
    variant
  } = props;

  const PalAccordion = PaletteAccordion.default || PaletteAccordion;

  // Compose onClick for accordion open action
  // -> handles invoking the action from DXL
  const handleOpen = useCallback(
    utils.composeHandleOpenClose( { action: expandAction, clickEventType: constants.DATACAPTURE_EVENT_TYPE.click }, { processEvents : datacapture.processEvents, setIsActive } ), [expandAction]
  );

  // Compose onClick for accordion close action
  // -> handles invoking the action from DXL
  const handleClose = useCallback(
    utils.composeHandleOpenClose( { action: collapseAction, clickEventType: constants.DATACAPTURE_EVENT_TYPE.click }, { processEvents : datacapture.processEvents, setIsActive } ), [collapseAction]
  );

  return (
    <PalAccordion
      ariaControls={ ariaControls }
      ariaLabelledBy={ ariaLabelledBy }
      disabled={ disabled }
      isActive={ isActive }
      headerChildren={ headerChildren }
      subTitle={ subTitle }
      titleTag={ titleTag }
      title={ title }
      externalOpenAction={ handleOpen }
      externalCloseAction={ handleClose }
      expandMore={ expandMore }
      setExpandMore={ setExpandMore }
      icon={ icon }
      variant={ variant }
    >
      { children }
    </PalAccordion>
  );
};

/**
   * Method to handle open and close actions for accordion
   * @param {object} data - Data passed as an argument
   * @param {object} data.action.dataCaptureData DXL action with variables set
   * @param {object} data.clickEventType type of dataCapture event
   * @param {object} methods methods
   * @param {function} methods.processEvents dataCapture process events
   * @param {function} methods.setIsActive set accordion active state
   */
export const composeHandleOpenClose = ( data, methods ) => () => {
  const { action, clickEventType } = data || {};
  const { processEvents, setIsActive } = methods || {};
  const { dataCaptureData } = action || {};

  if( setIsActive ){
    setIsActive( ( isActive ) => !isActive );
  }

  if( !processEvents || !dataCaptureData ){
    return;
  }
  processEvents( { dataCapture: dataCaptureData }, clickEventType );
};


/**
  * Property type definitions
  * @typedef AccordionProps
  * @type {object}
  * @property {string} ariaControls - Links the aria-control attribute in the accordion panel header to the id in the accordion panel body
  * @property {string} ariaLabelledBy - HTML id for aria-labelledby, which associates panel contents with the accordion title
  * @property {HTMLElement} children - Child node(s) that can be nested inside component
  * @property {boolean} disabled - Boolean that controls whether the accordion is disabled or not
  * @property {node} headerChildren - A React element to be rendered as the header children
  * @property {boolean} isActive - Panel is open when set to true. Close when set to false
  * @property {string} subTitle - Subtitle for the accordion's heading section
  * @property {Array.<string>} titleTag - Required html tag for title for the accordion's heading section
  * @property {string} title - Title for the accordion's heading section
  * @property {object} collapseAction - Action to perform on click to collapse accordion
  * @property {object} expandAction - Action to perform on click to expand accordion
  * @property {boolean} expandMore - Boolean that controls whether to see more is clicked or not
  * @property {function} setExpandMore - sets the value for expand more
  * @property {function} setIsActive - set the state for expand collapse
  * @property {function} icon - set the icon for accordion section
  * @property {function} variant - set the variant for accordion
  */
export const propTypes = {
  /** Links the aria-control attribute in the accordion panel header to the id in the accordion panel body */
  ariaControls: PropTypes.string,
  /** HTML id for aria-labelledby, which associates panel contents with the accordion title */
  ariaLabelledBy: PropTypes.string,
  /** Child node(s) that can be nested inside component */
  children: PropTypes.node,
  /**
    * disabled
    * 1) Panel is disabled when set to true. Able to click when set to false
    */
  disabled: PropTypes.bool,
  /**
    * is Active
    * 1) Panel is open when set to true. Close when set to false
    */
  isActive: PropTypes.bool,
  /** A React element to be rendered as the header children */
  headerChildren: PropTypes.node,
  /** subtitle for the accordion's heading section */
  subTitle: PropTypes.string,
  /** html tag for title for the accordion's heading section */
  titleTag: PropTypes.oneOf( ['p', 'h2', 'h3', 'h4', 'h5', 'h6'] ).isRequired,
  /** title for the accordion's heading section */
  title: PropTypes.string,
  /** action to perform on click to collapse accordion */
  collapseAction: PropTypes.object,
  /** action to perform on click to expand accordion */
  expandAction: PropTypes.object,
  /** Boolean that controls whether to see more is clicked or not */
  expandMore: PropTypes.bool,
  /** sets the value for expand more */
  setExpandMore: PropTypes.func,
  /** set the state for expand collapse */
  setIsActive: PropTypes.func,
  /** set the icon for accordion's section */
  icon: PropTypes.string,
  /** set the variant for accordion */
  variant: PropTypes.oneOf( ['compact', 'default', 'tiny'] )
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} ariaControls='' - Sets the default value of ariaControls
 * @property {string} ariaLabelledBy='' - Sets the default value of ariaLabelledBy
 * @property {HTMLElement} children=undefined - Sets the default value of children
 * @property {boolean} disabled=false - Boolean that controls whether the accordion is disabled or not
 * @property {node} headerChildren - set default element as the header children
 * @property {boolean} isActive=false - Sets the default value of isActive
 * @property {string} subTitle=undefined - Sets the default value of subTitle
 * @property {string} titleTag='h3' - Sets the default value of titleTag
 * @property {string} title=undefined - Sets the default value of title
 * @property {boolean} expandMore=false - Boolean that controls whether to see more is clicked or not
 * @property {function} setExpandMore=() => false - Sets the value for expand more
 */
export const defaultProps = {
  ariaControls: '',
  ariaLabelledBy: '',
  children: undefined,
  disabled: false,
  isActive: false,
  headerChildren: undefined,
  subTitle: undefined,
  titleTag: 'h3',
  title: undefined,
  expandMore: false,
  setExpandMore: () => false,
  icon: undefined,
  variant: 'default'
};

export default Accordion;